import * as React  from 'react';
import UserImage from '../../../components/UserImage/index';
import { ActorAvatar, ActorName, TransferParty, useOrg } from './actor';
import { combinedViewTime } from './dateKey';
import ActivityTitle from './activityTitle';
import ChangeRow from './changeRow';
import { getResolutionName } from '../utils';

/**
 * An Activity action: an assignee handover, an owner handover, or a resolution
 * change.
 *
 * A handover can move work between users, between organizations, or both, which
 * is why each side is rendered by TransferParty rather than assuming a user.
 *
 * The previous version returned undefined for an entry that was neither an
 * assignee nor an owner change, so a plain resolution change - closing an
 * activity, say - silently rendered nothing. Those now fall through to the
 * from/to row.
 */
export default function ActivityActionHistoryTemplate({ data, isPrintPreview }) {
    const change = data.change || {};
    const isAssigneeChange = change.field === "Assignee";
    const isResolutionChange = change.field === "Resolution";
    // Presence of a from/to id isn't enough: a plain Resolution change still
    // carries the activity's current user/org ids on both sides (often the "0"
    // sentinel for "no user"), which is not a handover. Only a real difference
    // between the from and to side is one.
    const isOwnerChange = !isAssigneeChange && !isResolutionChange && (
        change.from_user_id !== change.to_user_id ||
        change.from_org_id !== change.to_org_id
    );

    const heading = isAssigneeChange
        ? "changed activity assignee of"
        : isResolutionChange
            ? "changed resolution of"
            : isOwnerChange
                ? "changed activity owner of"
                : "updated activity";

    return (
        <div className="combinedListing detailListing">
            <span className="time_line_bar"></span>

            <div className="combinedCreatedDate detailCreatedDate">
                <ActorAvatar userId={data.actor_id} />
            </div>

            <div className="combinedRightBlock detailRightBlock resolutionChanged">
                <div className="combinedDescription detailDescription">
                    <div className="alignMiddle mb mb__8 flexWrap">
                        <ActorName userId={data.actor_id} inner="header" />
                        <span className="dateTime mr">
                            <label className="mrNone">{combinedViewTime(data.occurred_at)}</label>
                        </span>
                        {isResolutionChange ? <span className="mr">{heading}</span> : heading}
                        <ActivityTitle activity={data.activity} isPrintPreview={isPrintPreview} />
                    </div>

                    {isResolutionChange ? (
                        <p className="resolutionBlock">
                            From
                            <span className={`status ${getResolutionName((change.from_value || "").toLowerCase(), true)}`}> {change.from_value} </span>
                            to
                            <span className={`status ${getResolutionName((change.to_value || "").toLowerCase(), true)}`}> {change.to_value}</span>
                        </p>
                    ) : (isAssigneeChange || isOwnerChange) ? (
                        <div className={isAssigneeChange ? "users_involved userGroup col-3" : "userAssignee userGroup rowFlex alignMiddle col-3"}>
                            <ul className="flexGrow">
                                <li>
                                    <div className="userPic rowFlex">
                                        <TransferAvatar userId={change.from_user_id} orgId={change.from_org_id} />
                                        <div className="colFlex ml">
                                            <label>From</label>
                                            <TransferParty userId={change.from_user_id} orgId={change.from_org_id} />
                                        </div>
                                    </div>
                                </li>
                                <li>
                                    <div className="userPic rowFlex">
                                        <TransferAvatar userId={change.to_user_id} orgId={change.to_org_id} />
                                        <div className="colFlex ml">
                                            <label>To</label>
                                            <TransferParty userId={change.to_user_id} orgId={change.to_org_id} />
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    ) : (
                        <div className="update_information">
                            <ul className="logs_listing">
                                <ChangeRow change={data.change} />
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Avatar for one side of a handover: the user's when the work moved between
 * people, otherwise the organization's.
 */
function TransferAvatar({ userId, orgId }) {
    const org = useOrg(orgId);
    if (isRealId(userId)) {
        return <ActorAvatar userId={userId} />;
    }
    if (isRealId(orgId)) {
        return <UserImage profileUrl={org.imageUrl} className="profilePic" profileName={org.name} />;
    }
    return null;
}

function isRealId(id) {
    return !!id && id !== "0";
}
